import React, { useState, useEffect, useRef } from 'react';
import { AlertCircle, CheckCircle, X, Loader2, Trash2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { normalizeType } from '../../../utils/dataFormatter';

interface Invoice {
  invoiceId: string;
  zohoId: string;
  siteCode: string;
  type: string;
  invoiceNo: string;
  invoiceDate?: string;
  poNo?: string;
  paymentDueDate?: string;
  quarter: string;
  amount: string;
  status: string;
  createdAt: string;
  createdBy: string;
  siteDetails?: string;
}

interface SyncReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  fetchedInvoices: Invoice[];
  authToken: string;
  accountId: string;
  currentUserId: string;
  onSyncConfirmed: (invoices: Invoice[]) => void;
}

export function ZohoInvoiceSyncReviewModal({
  isOpen,
  onClose,
  fetchedInvoices,
  authToken,
  accountId,
  currentUserId,
  onSyncConfirmed,
}: SyncReviewModalProps) {
  const [isSaving, setIsSaving] = useState(false);
  const [editableInvoices, setEditableInvoices] = useState<Invoice[]>([]);
  
  // Drag scroll state
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(0);
  const completeTableRef = useRef<HTMLDivElement>(null);
  const incompleteTableRef = useRef<HTMLDivElement>(null);

  // Sync editableInvoices when fetchedInvoices changes (modal opens)
  useEffect(() => {
    if (isOpen && fetchedInvoices.length > 0) {
      // Normalize type field for all invoices
      const normalizedInvoices = fetchedInvoices.map(inv => ({
        ...inv,
        type: normalizeType(inv.type),
      }));
      setEditableInvoices(normalizedInvoices);
    }
  }, [isOpen, fetchedInvoices]);

  // Separate invoices into satisfied (complete) and unsatisfied (incomplete)
  const categorizeInvoices = (invoices: Invoice[]) => {
    return {
      satisfied: invoices.filter(
        (inv) =>
          inv.siteCode &&
          inv.type &&
          inv.invoiceNo &&
          inv.quarter &&
          inv.amount &&
          inv.status &&
          inv.invoiceDate &&
          inv.poNo &&
          inv.paymentDueDate
      ),
      unsatisfied: invoices.filter(
        (inv) =>
          !inv.siteCode ||
          !inv.type ||
          !inv.invoiceNo ||
          !inv.quarter ||
          !inv.amount ||
          !inv.status ||
          !inv.invoiceDate ||
          !inv.poNo ||
          !inv.paymentDueDate
      ),
    };
  };

  const categorizedInvoices = categorizeInvoices(editableInvoices);

  const handleInvoiceChange = (index: number, field: keyof Invoice, value: any) => {
    const updated = [...editableInvoices];
    
    // Normalize type field when it's being changed
    if (field === 'type' && typeof value === 'string') {
      updated[index] = {
        ...updated[index],
        [field]: normalizeType(value),
      };
    } else {
      updated[index] = {
        ...updated[index],
        [field]: value,
      };
    }
    setEditableInvoices(updated);
  };

  const addEmptyInvoice = () => {
    const newInvoice: Invoice = {
      invoiceId: `temp-${Date.now()}`,
      zohoId: '',
      siteCode: '',
      type: '',
      invoiceNo: '',
      invoiceDate: '',
      poNo: '',
      paymentDueDate: '',
      quarter: '',
      amount: '',
      status: 'pending',
      createdAt: new Date().toISOString(),
      createdBy: currentUserId || 'unknown',
    };
    setEditableInvoices([...editableInvoices, newInvoice]);
  };

  const removeInvoice = (invoiceId: string) => {
    setEditableInvoices(editableInvoices.filter((inv) => inv.invoiceId !== invoiceId));
  };

    // ========================================
  // BACKEND-ALIGNED PARSING LOGIC
  // ========================================

  // Normalize input: replaces newlines, hyphens, and collapses spaces
  const normalize = (input: string): string => {
    let s = input.replace(/[\r\n]/g, ' ');           // Replace line breaks with spaces
    s = s.replace(/-/g, ' ');                         // Replace hyphens with spaces
    
    // Insert space before AMC or Refilling if glued to other text
    s = s.replace(/([A-Za-z0-9])(AMC|Refilling)/gi, '$1 $2');
    
    // Collapse multiple spaces into single space
    s = s.replace(/\s+/g, ' ');
    
    return s.trim();
  };

  // Check if a token is noise (should be ignored as a site code)
  const isNoise = (token: string): boolean => {
    const l = token.toLowerCase();
    
    // Ignore type keywords
    if (l === 'amc' || l === 'refilling') {
      return true;
    }
    
    // Ignore quarter (Q1-Q4)
    if (/^q[1-4]$/i.test(l)) {
      return true;
    }
    
    // Ignore months (with optional year suffix)
    const monthRegex = /^(jan|feb|mar|apr|april|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*\d*$/i;
    if (monthRegex.test(l)) {
      return true;
    }
    
    return false;
  };

  // Extract site code: looks for patterns like "A428" or "12345"
  const extractSiteCodeStructured = (text: string): string => {
    // Match: Letter + 3+ digits OR Pure numeric 3+ digits
    const siteRegex = /\b([A-Za-z][0-9]{3,}|[0-9]{3,})\b/g;
    const matches = text.match(siteRegex) || [];
    
    for (const candidate of matches) {
      if (!isNoise(candidate)) {
        return candidate;
      }
    }
    
    return '';
  };

  // Parse Site Details field to extract Quarter, Type, and Site Code
  const parseSiteDetails = (siteDetails: string) => {
    const result = { quarter: '', type: 'AMC', siteCode: '' }; // Default type is AMC
    
    if (!siteDetails || siteDetails.trim() === '') {
      return result;
    }
    
    const normalized = normalize(siteDetails);
    
    // === 1️⃣ Extract Quarter (Q1–Q4) ===
    const quarterRegex = /\b(Q[1-4])\b/i;
    const quarterMatch = normalized.match(quarterRegex);
    if (quarterMatch) {
      result.quarter = quarterMatch[1].toUpperCase();
    }
    
    // === 2️⃣ Extract Invoice Type ===
    // Refilling has higher priority
    const typeRegex = /\b(Refilling|AMC)\b/i;
    const typeMatch = normalized.match(typeRegex);
    if (typeMatch) {
      result.type = typeMatch[1].charAt(0).toUpperCase() + typeMatch[1].slice(1).toLowerCase();
    }
    
    // === 3️⃣ Extract Site Code ===
    result.siteCode = extractSiteCodeStructured(normalized);
    
    return result;
  };


  const autoParseIncompleteInvoices = () => {
    const updated = editableInvoices.map((invoice) => {
      // Only parse if siteDetails exists and fields are missing
      if (invoice.siteDetails && (!invoice.quarter || !invoice.type || !invoice.siteCode)) {
        const parsed = parseSiteDetails(invoice.siteDetails);
        return {
          ...invoice,
          quarter: invoice.quarter || parsed.quarter,
          type: invoice.type || parsed.type,
          siteCode: invoice.siteCode || parsed.siteCode,
        };
      }
      return invoice;
    });
    setEditableInvoices(updated);
  };

  // Drag scroll handlers
  const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(null);

  const handleMouseDown = (e: React.MouseEvent, containerRef: React.RefObject<HTMLDivElement | null>) => {
    setIsDragging(true);
    setDragStart(e.clientX);
    setScrollContainer(containerRef.current);
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (!scrollContainer) return;
      
      const dragDistance = e.clientX - dragStart;
      scrollContainer.scrollLeft -= dragDistance;
      setDragStart(e.clientX);
    };

    const handleGlobalMouseUp = () => {
      setIsDragging(false);
      setScrollContainer(null);
    };

    document.addEventListener('mousemove', handleGlobalMouseMove);
    document.addEventListener('mouseup', handleGlobalMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDragging, dragStart, scrollContainer]);

  const handleConfirmSync = async () => {
    try {
      setIsSaving(true);
      // Send edited invoices to backend for storage
      const response = await fetch(`/api/v1/account/${accountId}/zoho-invoices/sync-bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          invoices: editableInvoices,
          syncType: 'INITIAL_FETCH',
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to sync invoices');
      }

      // Notify parent component of successful sync
      onSyncConfirmed(editableInvoices);
      onClose();
    } catch (error) {
      console.error('Error syncing invoices:', error);
      alert('Failed to sync invoices. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] max-h-[95vh] w-full overflow-hidden flex flex-col bg-white [&>button]:hidden">
        <DialogHeader className="sticky top-0 bg-white pb-4 border-b z-20 flex-shrink-0">
          <div className="flex justify-between items-start gap-4">
            <div>
              <DialogTitle>Review & Edit Fetched Invoices</DialogTitle>
              <DialogDescription className="mt-2">
                Total: {editableInvoices.length} | Complete: {categorizedInvoices.satisfied.length} | Incomplete: {categorizedInvoices.unsatisfied.length}
              </DialogDescription>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={24} />
            </button>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-6 px-0">
          {/* COMPLETE DATA SECTION */}
          <div className="bg-green-50">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <h3 className="font-semibold text-green-900">
                Complete Data ({categorizedInvoices.satisfied.length})
              </h3>
            </div>

            {categorizedInvoices.satisfied.length === 0 ? (
              <p className="text-sm text-green-700 p-4">No complete invoices</p>
            ) : (
              <div 
                ref={completeTableRef}
                className="rounded-lg border border-green-200 bg-white overflow-x-auto cursor-grab active:cursor-grabbing"
                onMouseDown={(e) => handleMouseDown(e, completeTableRef)}
              >
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-green-100 border-b border-green-200 sticky top-0">
                      <th className="px-4 py-3 text-left font-semibold text-green-900 whitespace-nowrap min-w-[120px]">Invoice No</th>
                      <th className="px-4 py-3 text-left font-semibold text-green-900 whitespace-nowrap min-w-[110px]">Site Code</th>
                      <th className="px-4 py-3 text-left font-semibold text-green-900 whitespace-nowrap min-w-[100px]">Type</th>
                      <th className="px-4 py-3 text-left font-semibold text-green-900 whitespace-nowrap min-w-[100px]">Quarter</th>
                      <th className="px-4 py-3 text-left font-semibold text-green-900 whitespace-nowrap min-w-[100px]">Amount</th>
                      <th className="px-4 py-3 text-left font-semibold text-green-900 whitespace-nowrap min-w-[100px]">Status</th>
                      <th className="px-4 py-3 text-left font-semibold text-green-900 whitespace-nowrap min-w-[120px]">Invoice Date</th>
                      <th className="px-4 py-3 text-left font-semibold text-green-900 whitespace-nowrap min-w-[100px]">PO No</th>
                      <th className="px-4 py-3 text-left font-semibold text-green-900 whitespace-nowrap min-w-[100px]">Due Date</th>
                      <th className="px-4 py-3 text-center font-semibold text-green-900 w-16 sticky right-0 bg-green-100 z-10 border-l border-green-200">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categorizedInvoices.satisfied.map((invoice, idx) => {
                      const realIndex = editableInvoices.indexOf(invoice);
                      return (
                        <tr key={invoice.invoiceId} className="border-b border-green-100 hover:bg-green-50">
                          <td className="px-4 py-2">
                            <input
                              type="text"
                              value={invoice.invoiceNo}
                              onChange={(e) => handleInvoiceChange(realIndex, 'invoiceNo', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:outline-none text-sm"
                            />
                          </td>
                          <td className="px-4 py-2">
                            <input
                              type="text"
                              value={invoice.siteCode}
                              onChange={(e) => handleInvoiceChange(realIndex, 'siteCode', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:outline-none text-sm"
                            />
                          </td>
                          <td className="px-4 py-2">
                            <select
                              value={invoice.type || ''}
                              onChange={(e) => handleInvoiceChange(realIndex, 'type', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:outline-none text-sm"
                            >
                              <option value="">Select Type</option>
                              <option value="AMC">AMC</option>
                              <option value="Refilling">Refilling</option>
                            </select>
                          </td>
                          <td className="px-4 py-2">
                            <input
                              type="text"
                              value={invoice.quarter}
                              onChange={(e) => handleInvoiceChange(realIndex, 'quarter', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:outline-none text-sm"
                            />
                          </td>
                          <td className="px-4 py-2">
                            <input
                              type="text"
                              value={invoice.amount}
                              onChange={(e) => handleInvoiceChange(realIndex, 'amount', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:outline-none text-sm"
                            />
                          </td>
                          <td className="px-4 py-2">
                            <select
                              value={invoice.status}
                              onChange={(e) => handleInvoiceChange(realIndex, 'status', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:outline-none text-sm"
                            >
                              <option value="paid">Paid</option>
                              <option value="pending">Pending</option>
                              <option value="overdue">Overdue</option>
                              <option value="unpaid">Unpaid</option>
                            </select>
                          </td>
                          <td className="px-4 py-2">
                            <input
                              type="date"
                              value={invoice.invoiceDate || ''}
                              onChange={(e) => handleInvoiceChange(realIndex, 'invoiceDate', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:outline-none text-sm"
                            />
                          </td>
                          <td className="px-4 py-2">
                            <input
                              type="text"
                              value={invoice.poNo || ''}
                              onChange={(e) => handleInvoiceChange(realIndex, 'poNo', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:outline-none text-sm"
                            />
                          </td>
                          <td className="px-4 py-2">
                            <input
                              type="date"
                              value={invoice.paymentDueDate || ''}
                              onChange={(e) => handleInvoiceChange(realIndex, 'paymentDueDate', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:outline-none text-sm"
                            />
                          </td>
                          <td className="px-4 py-2 text-center sticky right-0 bg-green-50 border-l border-green-100 z-10">
                            <button
                              onClick={() => removeInvoice(invoice.invoiceId)}
                              className="p-2 hover:bg-red-50 rounded-lg transition-colors text-red-600 hover:text-red-700 inline-block"
                              title="Remove Invoice"
                            >
                              <Trash2 size={18} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* INCOMPLETE DATA SECTION */}
          <div className="border rounded-lg p-4 bg-yellow-50">
            <div className="flex items-center gap-2 mb-4 justify-between">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-yellow-600" />
                <h3 className="font-semibold text-yellow-900">
                  Incomplete Data ({categorizedInvoices.unsatisfied.length})
                </h3>
              </div>
              {categorizedInvoices.unsatisfied.length > 0 && (
                <button
                  onClick={autoParseIncompleteInvoices}
                  className="px-3 py-1.5 bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-medium rounded transition-colors"
                >
                  Auto-Parse from Site Details
                </button>
              )}
            </div>

            {categorizedInvoices.unsatisfied.length === 0 ? (
              <p className="text-sm text-yellow-700 p-4">No incomplete invoices</p>
            ) : (
              <div 
                ref={incompleteTableRef}
                className="rounded-lg border border-yellow-200 bg-white overflow-x-auto cursor-grab active:cursor-grabbing"
                onMouseDown={(e) => handleMouseDown(e, incompleteTableRef)}
              >
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-yellow-100 border-b border-yellow-200 sticky top-0">
                      <th className="px-4 py-3 text-left font-semibold text-yellow-900 whitespace-nowrap min-w-[120px]">Invoice No</th>
                      <th className="px-4 py-3 text-left font-semibold text-yellow-900 whitespace-nowrap min-w-[110px]">Site Code</th>
                      <th className="px-4 py-3 text-left font-semibold text-yellow-900 whitespace-nowrap min-w-[100px]">Type</th>
                      <th className="px-4 py-3 text-left font-semibold text-yellow-900 whitespace-nowrap min-w-[100px]">Quarter</th>
                      <th className="px-4 py-3 text-left font-semibold text-yellow-900 whitespace-nowrap min-w-[100px]">Amount</th>
                      <th className="px-4 py-3 text-left font-semibold text-yellow-900 whitespace-nowrap min-w-[100px]">Status</th>
                      <th className="px-4 py-3 text-left font-semibold text-yellow-900 whitespace-nowrap min-w-[120px]">Invoice Date</th>
                      <th className="px-4 py-3 text-left font-semibold text-yellow-900 whitespace-nowrap min-w-[100px]">PO No</th>
                      <th className="px-4 py-3 text-left font-semibold text-yellow-900 whitespace-nowrap min-w-[100px]">Due Date</th>
                      <th className="px-4 py-3 text-left font-semibold text-yellow-900 whitespace-nowrap min-w-[150px]">Site Details</th>
                      <th className="px-4 py-3 text-center font-semibold text-yellow-900 w-16 sticky right-0 bg-yellow-100 z-10 border-l border-yellow-200">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categorizedInvoices.unsatisfied.map((invoice, idx) => {
                      const realIndex = editableInvoices.indexOf(invoice);

                      return (
                        <tr key={invoice.invoiceId} className="border-b border-yellow-100 hover:bg-yellow-50">
                          <td className="px-4 py-2">
                            <input
                              type="text"
                              value={invoice.invoiceNo || ''}
                              onChange={(e) => handleInvoiceChange(realIndex, 'invoiceNo', e.target.value)}
                              placeholder="Required"
                              className={`w-full px-2 py-1 border rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none text-sm ${!invoice.invoiceNo ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                            />
                          </td>
                          <td className="px-4 py-2">
                            <input
                              type="text"
                              value={invoice.siteCode || ''}
                              onChange={(e) => handleInvoiceChange(realIndex, 'siteCode', e.target.value)}
                              placeholder="Required"
                              className={`w-full px-2 py-1 border rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none text-sm ${!invoice.siteCode ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                            />
                          </td>
                          <td className="px-4 py-2">
                            <select
                              value={invoice.type || ''}
                              onChange={(e) => handleInvoiceChange(realIndex, 'type', e.target.value)}
                              className={`w-full px-2 py-1 border rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none text-sm ${!invoice.type ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                            >
                              <option value="">Select Type (Required)</option>
                              <option value="AMC">AMC</option>
                              <option value="Refilling">Refilling</option>
                            </select>
                          </td>
                          <td className="px-4 py-2">
                            <input
                              type="text"
                              value={invoice.quarter || ''}
                              onChange={(e) => handleInvoiceChange(realIndex, 'quarter', e.target.value)}
                              placeholder="Required"
                              className={`w-full px-2 py-1 border rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none text-sm ${!invoice.quarter ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                            />
                          </td>
                          <td className="px-4 py-2">
                            <input
                              type="text"
                              value={invoice.amount || ''}
                              onChange={(e) => handleInvoiceChange(realIndex, 'amount', e.target.value)}
                              placeholder="Required"
                              className={`w-full px-2 py-1 border rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none text-sm ${!invoice.amount ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                            />
                          </td>
                          <td className="px-4 py-2">
                            <select
                              value={invoice.status || ''}
                              onChange={(e) => handleInvoiceChange(realIndex, 'status', e.target.value)}
                              className={`w-full px-2 py-1 border rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none text-sm ${!invoice.status ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                            >
                              <option value="">Select</option>
                              <option value="paid">Paid</option>
                              <option value="pending">Pending</option>
                              <option value="overdue">Overdue</option>
                              <option value="unpaid">Unpaid</option>
                            </select>
                          </td>
                          <td className="px-4 py-2">
                            <input
                              type="date"
                              value={invoice.invoiceDate || ''}
                              onChange={(e) => handleInvoiceChange(realIndex, 'invoiceDate', e.target.value)}
                              className={`w-full px-2 py-1 border rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none text-sm ${!invoice.invoiceDate ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                            />
                          </td>
                          <td className="px-4 py-2">
                            <input
                              type="text"
                              value={invoice.poNo || ''}
                              onChange={(e) => handleInvoiceChange(realIndex, 'poNo', e.target.value)}
                              className={`w-full px-2 py-1 border rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none text-sm ${!invoice.poNo ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                            />
                          </td>
                          <td className="px-4 py-2">
                            <input
                              type="date"
                              value={invoice.paymentDueDate || ''}
                              onChange={(e) => handleInvoiceChange(realIndex, 'paymentDueDate', e.target.value)}
                              className={`w-full px-2 py-1 border rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none text-sm ${!invoice.paymentDueDate ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                            />
                          </td>
                          <td className="px-4 py-2">
                            <input
                              type="text"
                              value={invoice.siteDetails || ''}
                              onChange={(e) => handleInvoiceChange(realIndex, 'siteDetails', e.target.value)}
                              placeholder="e.g., Q3 APRIL25 AMC A428"
                              className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none text-sm"
                            />
                          </td>
                          <td className="px-4 py-2 text-center sticky right-0 bg-yellow-50 border-l border-yellow-100 z-10">
                            <button
                              onClick={() => removeInvoice(invoice.invoiceId)}
                              className="p-2 hover:bg-red-50 rounded-lg transition-colors text-red-600 hover:text-red-700 inline-block"
                              title="Remove Invoice"
                            >
                              <Trash2 size={18} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* ACTION BUTTONS - Sticky at bottom */}
        <div className="flex gap-3 p-6 border-t bg-white flex-shrink-0">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSaving}
            className="flex-1"
          >
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSync}
            disabled={isSaving || editableInvoices.length === 0}
            className="flex-1 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Syncing... ({editableInvoices.length})
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Confirm & Sync ({editableInvoices.length})
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
